import api from '@common/api';
import session from '@common/session';
import { createStore, useStore } from 'zustand';
import { persist } from 'zustand/middleware';
import { AutoInvestmentsModel } from './AutoInvestmentsModel';
import { BankAccountsModel } from './BankAccountsModel';
import { InboxModel } from './InboxModel';
import { InvestorAccountModel } from './InvestorAccountModel';
import { MyProfileModel } from './MyProfileModel';
import { SubscriptionModel } from './SubscriptionModel';
import { TradesModel } from './TradesModel';
import { TransactionsModel } from './TransactionsModel';

interface ConsolidatedModelProps {
  ttl?: number;
}
interface ConsolidatedModelActions {
  fetchData: () => Promise<void>;
}

export const ConsolidatedModel = createStore(
  persist<ConsolidatedModelProps & ConsolidatedModelActions>(
    () => ({
      fetchData: async () => {
        const investorId = session.get()?.investorId;
        if (investorId) {
          const consolidatedInvestor = await api.getConsolidatedInvestor({ id: investorId });
          InvestorAccountModel.getState().setInvestorAccount(consolidatedInvestor?.investor ?? null);
          InvestorAccountModel.getState().setAccountValue(consolidatedInvestor?.acc_value ?? null);
          InvestorAccountModel.getState().setSubscriptionOffer(consolidatedInvestor?.subscription_offer ?? null);
          BankAccountsModel.getState().setBankAccounts(consolidatedInvestor?.bank_accounts ?? []);
          TradesModel.getState().setTrades(consolidatedInvestor?.trades ?? []);
          TransactionsModel.getState().setTransactions(consolidatedInvestor?.transactions ?? []);
          TransactionsModel.getState().setDividends(consolidatedInvestor?.dividends ?? null);
          MyProfileModel.getState().setMyProfile(consolidatedInvestor?.user_profile ?? null);
          SubscriptionModel.getState().setSubscription(consolidatedInvestor?.subscription);
          SubscriptionModel.getState().setSubscriptions(consolidatedInvestor?.subscriptions ?? []);
          AutoInvestmentsModel.getState().setAutoInvestments(consolidatedInvestor?.auto_invests ?? []);
          InboxModel.getState().checkForAlerts(consolidatedInvestor?.investor ?? null);
          InboxModel.getState().checkForMessages();
          InboxModel.getState().checkForIntercomMessages();

          // TODO: Set Goals when Goals module is added
        }
      },
    }),
    {
      getStorage: () => localStorage,
      name: 'consolidated-model',
    },
  ),
);

export function useConsolidatedModel(): ConsolidatedModelProps & ConsolidatedModelActions;
export function useConsolidatedModel<T>(
  selector: (state: ConsolidatedModelProps & ConsolidatedModelActions) => T,
  equals?: (a: T, b: T) => boolean,
): T;
export function useConsolidatedModel<T>(
  selector?: (state: ConsolidatedModelProps & ConsolidatedModelActions) => T,
  equals?: (a: T, b: T) => boolean,
) {
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  return useStore(ConsolidatedModel, selector!, equals);
}
